import { Component, createRef, Fragment } from 'preact';

import {
	disableBodyScroll,
	enableBodyScroll,
	clearAllBodyScrollLocks
} from 'body-scroll-lock';
import classnames from 'classnames';

import { isDesktop, isMobile } from '../../lib/Media';
import ViewContext from '../../view/ViewContext';
import Button from '../button/Button';
import Icon from '../icon/Icon';
import Portal from '../portal/Portal';

export const INNER_HEIGHT_VAR = '--ribbon-inner-height';
export const MENU_QUERY_PARAMETER = 'ribbon--menu';
const OVERFLOW_Y_PROP = 'overflow-y';

export default class Menu extends Component {
	static get contextType() {
		return ViewContext;
	}

	constructor() {
		super();

		this._contentRef = createRef();

		this._opened = new Set();
		this._menuRef = createRef();
	}

	componentDidMount() {
		!this.props.hidden && this._menuRef?.current?.focus();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.hidden !== this.props.hidden) {
			this._fixIOSHeight();

			const { current: contentElm } = this._contentRef;

			if (!this.props.hidden && isMobile()) {
				contentElm &&
					disableBodyScroll(contentElm, {
						reserveScrollBarGap: isDesktop()
					});
				this._removeScrollbar();
			} else {
				contentElm && enableBodyScroll(contentElm);
				this._resetScrollbar();
			}

			if (!this.props.hidden && this._menuRef.current) {
				this._menuRef.current.focus();
			}
		}
	}

	componentWillUnmount() {
		clearAllBodyScrollLocks();
		this._resetScrollbar();
	}

	render() {
		const { menu } = this.context.props;

		if (!Array.isArray(menu) || !menu.length) {
			return null;
		}

		return (
			// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
			<div
				className={classnames({
					'ribbon-menu': true,
					'ribbon-menu--hidden': this.props.hidden
				})}
				onClick={event => this.onBlankClick(event)}
			>
				<div
					className={classnames({
						'ribbon-menu__content': true,
						'ribbon-menu__content--hidden': this.props.hidden
					})}
					ref={this._contentRef}
				>
					<h6 className='ribbon--blind' id='ribbon-menu-0'>
						Hlavní menu
					</h6>
					<button
						type='button'
						className='ribbon--blind'
						onClick={() => this.props.onClose()}
					>
						Zavřít menu
					</button>
					<Portal className='ribbon-menu__top' />
					<ul className='ribbon-menu__primary'>
						{menu.map((item, index) => this._renderItem(item, index))}
					</ul>
					<Portal className='ribbon-menu__bottom' />
				</div>
			</div>
		);
	}

	_renderItem({ dataDotData, href, submenu, target, text }, index = null) {
		const isOpened = this._opened.has(index);
		const optional = {};

		if (dataDotData) {
			if (typeof dataDotData !== 'string') {
				dataDotData = JSON.stringify(dataDotData);
			}

			optional['data-dot-data'] = dataDotData;
		}

		if (target) {
			optional.target = target;
		}

		const attr = {};
		if (!index) {
			attr.tabIndex = '-1';
		}

		if (index === 0) {
			optional.ref = this._menuRef;
		}

		return (
			<li className='ribbon-menu__item' {...attr}>
				<a
					className='ribbon-menu__link'
					href={href}
					onClick={() => this.props.onClose()}
					data-dot='menu-item'
					{...optional}
				>
					{text}
				</a>
				{submenu && !!submenu.length && (
					<Fragment>
						<Button
							className={classnames({
								'ribbon-menu__button': true,
								'ribbon-menu__button--up': isOpened
							})}
							onClick={() => this._toggleMenu(index)}
						>
							<Icon icon='arrow' />
						</Button>
						<ul
							className={classnames({
								'ribbon-menu__secondary': true,
								'ribbon-menu__secondary--hidden': !isOpened
							})}
						>
							{submenu.map(item => this._renderItem({ ...item, submenu: null }))}
						</ul>
					</Fragment>
				)}
			</li>
		);
	}

	onBlankClick(event) {
		const { onClose } = this.props;

		if (event.target === event.currentTarget && typeof onClose === 'function') {
			onClose();
		}
	}

	/**
	 * @see https://stackoverflow.com/questions/56622926/safari-viewport-bug-issues-with-fixed-position-and-bottom-or-top
	 */
	_fixIOSHeight() {
		const { style: documentElementStyle } = document.documentElement;

		documentElementStyle.setProperty(INNER_HEIGHT_VAR, `${window.innerHeight}px`);
	}

	_removeScrollbar() {
		document.documentElement.style.setProperty(OVERFLOW_Y_PROP, 'unset');
	}

	_resetScrollbar() {
		document.documentElement.style.removeProperty(OVERFLOW_Y_PROP);
	}

	_toggleMenu(index) {
		const { _opened } = this;

		if (_opened.has(index)) {
			_opened.delete(index);
		} else {
			_opened.add(index);
		}

		this.forceUpdate();
	}
}
